/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Variant-serving img
 */

import React from "react";
import type { ImgHTMLAttributes, SyntheticEvent } from "react";
import type { ImageVariantSize } from "@lemonadetraining/contracts";
import type { ImageSurface } from "../constants/imageAnalyticsEvents";
// Deliberately imported by PACKAGE name, not a relative path: every Jest
// consumer then resolves the hook through the single moduleNameMapper
// passthrough — the real hook's import chain initializes Firebase at module
// scope, which jsdom suites must never load.
import { useImageVariant } from "@lemonadetraining/web-shared/hooks/useImageVariant";

/* `crossOrigin` is withheld: the no-flash guarantee relies on the img request
 * reusing the hook's no-cors preload cache entry. */
type VariantImgProps = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "crossOrigin"> & {
  src?: string | null;
  size: ImageVariantSize;
  surface: ImageSurface;
};

/**
 * Drop-in <img> that serves the size-appropriate image variant: the original
 * renders immediately, the PRELOADED variant swaps in without flashing, and
 * a failed variant swaps back. The caller's own onError fires ONLY when the
 * ORIGINAL fails (a genuinely broken image the caller may want to placeholder)
 * — a failed variant is the hook's business and self-heals. Renders nothing
 * without a src.
 */
const VariantImg = ({ src, size, surface, onError, alt, ...props }: VariantImgProps) => {
  const variant = useImageVariant(src ?? null, size, surface);
  if (!variant.src) return null;
  const showingVariant = variant.src !== src;
  const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
    if (showingVariant) {
      variant.onError();
      return;
    }
    onError?.(event);
  };
  return <img {...props} alt={alt} src={variant.src} onError={handleError} />;
};

export default VariantImg;
